import { Dispatch, SetStateAction, useCallback, useState } from 'react'
import { NavigateOptions, useSearchParams } from 'react-router-dom'

type UseQueryStateReturnType<T> = [T, Dispatch<SetStateAction<T>>]

interface IQueryParamConfig {
  dataType: 'number' | 'string' | 'object'
}

const useQueryState = <T>(
  key: string,
  initialValue: T,
  config: IQueryParamConfig = { dataType: 'string' },
  navigateOpts: NavigateOptions = { replace: false }
): UseQueryStateReturnType<T> => {
  const [searchParams, setSearchParams] = useSearchParams()
  const parameterValue = searchParams.get(key)

  const [state, setState] = useState<T>(parameterValue ? decodeQueryValue(parameterValue, config) : initialValue)

  const handleChange = useCallback(
    (newValue: T) => {
      setState(newValue)
      const newSearchParams = new URLSearchParams(window.location.search)
      if (newValue === null) {
        newSearchParams.delete(key)
      } else {
        newSearchParams.set(key, encodeQueryValue(newValue, config))
      }

      setSearchParams(newSearchParams, navigateOpts)
    },
    [config]
  )

  return [state, handleChange]
}

const decodeQueryValue = (value: string, config: IQueryParamConfig) => {
  if (!value) return null

  switch (config.dataType) {
    case 'number':
      return +value
    case 'string':
      return value?.toString()
    case 'object':
      return JSON.parse(value)
  }
}

const encodeQueryValue = (value: any, config: IQueryParamConfig) => {
  if (!value) return null

  switch (config.dataType) {
    case 'number':
      return value + ''
    case 'string':
      return value?.toString()
    case 'object':
      return JSON.stringify(value)
  }
}

export default useQueryState
